import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { useSettingsRHF } from "../hooks/useSettingsRHF";
import { profileSchema } from "../types/settingsSchemas";
import type { ProfileSettingsData } from "../types/settings";

/**
 * Field classification for Profile form (two-tier visibility)
 *
 * Essential: shown during onboarding + Settings
 * Advanced: shown only in Settings (behind ShowMoreToggle)
 */
export const PROFILE_ESSENTIAL_FIELDS = [
  "yearOfBirth",
  "isRetired",
  "province",
  "retirementAge",
  "lifeExpectancy",
] as const;

export const PROFILE_ADVANCED_FIELDS = [
  "maritalStatus",
  "spouseYearOfBirth",
  "cppStartAge",
  "oasStartAge",
  "yearsInCanadaAfterAge18",
] as const;

/**
 * Shared logic for Profile functionality
 * Handles form state, validation (via Zod + RHF), and data management with auto-save
 */
export function useProfileLogic() {
  const { user, userProfile } = useAuth();
  const {
    formData: settingsData,
    handleChange: rhfHandleChange,
    handleBlur: rhfHandleBlur,
    getError,
    loading,
    form,
  } = useSettingsRHF<ProfileSettingsData>("profile", profileSchema);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Combine user profile data with settings data for display
  // For new users (onboarding), userProfile might be undefined - use auth user data as fallback
  const formData = {
    name: userProfile
      ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
      : "",
    email: userProfile?.email ?? user!.email,
    yearOfBirth: settingsData.yearOfBirth,
    isRetired: settingsData.isRetired,
    province: settingsData.province,
    retirementAge: settingsData.retirementAge,
    lifeExpectancy: settingsData.lifeExpectancy,
    maritalStatus: settingsData.maritalStatus,
    spouseYearOfBirth: settingsData.spouseYearOfBirth,
    cppStartAge: settingsData.cppStartAge,
    oasStartAge: settingsData.oasStartAge,
    yearsInCanadaAfterAge18: settingsData.yearsInCanadaAfterAge18,
  };

  /** Unified change handler — updates form via RHF (age re-derived by SettingsContext) */
  const handleChange = (
    field: string,
    value: number | boolean | string | null | undefined
  ) => {
    rhfHandleChange(field, value);
  };

  /** Unified blur handler — validates via Zod + saves to Firestore */
  const handleBlur = (
    field: string,
    value: number | boolean | string | null | undefined
  ) => {
    rhfHandleBlur(field, value);
  };

  /** Select field handler — saves immediately on change (no separate blur needed) */
  const handleSelectChange = (
    field: string,
    value: string | boolean | null | undefined
  ) => {
    rhfHandleChange(field, value);
    rhfHandleBlur(field, value);
  };

  const toggleAdvanced = () => setShowAdvanced((prev) => !prev);

  return {
    formData,
    handleChange,
    handleBlur,
    handleSelectChange,
    showAdvanced,
    toggleAdvanced,
    getError,
    loading,
    form,
  };
}
